import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Navigation, Car, Users, Search, Route as RouteIcon, Info } from 'lucide-react';
import { fadeInUp } from '../../lib/animations';
import DecimalInput from '../ui/DecimalInput';


interface Location {
  display_name: string;
  lat: string;
  lon: string;
}

const CalculadoraViagem: React.FC = () => {
  const [origemStr, setOrigemStr] = useState('');
  const [destinoStr, setDestinoStr] = useState('');

  const [origemLoc, setOrigemLoc] = useState<Location | null>(null);
  const [destinoLoc, setDestinoLoc] = useState<Location | null>(null);

  const [isSearching, setIsSearching] = useState(false);
  const [distanceKm, setDistanceKm] = useState<number | null>(null);
  const [durationMin, setDurationMin] = useState<number | null>(null);

  // Settings
  const [consumo, setConsumo] = useState(12);
  const [precoCombustivel, setPrecoCombustivel] = useState(5.50);
  const [pedagio, setPedagio] = useState(0);
  const [passageiros, setPassageiros] = useState(4);
  const [idaEVolta, setIdaEVolta] = useState(true);

  const searchLocation = async (query: string): Promise<Location | null> => {
    if (!query) return null;
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=br&limit=1`);
      const data = await res.json();
      if (data && data.length > 0) return data[0] as Location;
      return null;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  const handleRouteSearch = async () => {
    setIsSearching(true);
    try {
      let loc1 = origemLoc;
      let loc2 = destinoLoc;

      if (!loc1 || origemStr !== loc1.display_name) {
        loc1 = await searchLocation(origemStr);
        setOrigemLoc(loc1);
        if (loc1) setOrigemStr(loc1.display_name);
      }
      if (!loc2 || destinoStr !== loc2.display_name) {
        loc2 = await searchLocation(destinoStr);
        setDestinoLoc(loc2);
        if (loc2) setDestinoStr(loc2.display_name);
      }

      if (!loc1 || !loc2) {
        alert('Não foi possível encontrar uma das cidades. Tente ser mais específico (Ex: Campinas, SP).');
        setIsSearching(false);
        return;
      }

      const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${loc1.lon},${loc1.lat};${loc2.lon},${loc2.lat}?overview=false`);
      const data = await res.json();

      if (data.routes && data.routes.length > 0) {
        setDistanceKm(data.routes[0].distance / 1000);
        setDurationMin(Math.round(data.routes[0].duration / 60));
      } else {
        alert('Não foi possível traçar uma rota de carro entre estes locais.');
      }
    } catch (e) {
      alert('Erro ao calcular rota.');
      console.error(e);
    }
    setIsSearching(false);
  };

  const distBase = distanceKm || 0;
  const distTotal = idaEVolta ? distBase * 2 : distBase;
  const litros = consumo > 0 ? distTotal / consumo : 0;
  const gastoCombustivel = litros * precoCombustivel;
  const gastoTotal = gastoCombustivel + pedagio;
  const perCapita = passageiros > 0 ? gastoTotal / passageiros : 0;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Container Principal */}
      <motion.div variants={fadeInUp} className="overflow-hidden rounded-2xl bg-slate-900 shadow-xl shadow-slate-900/40 border border-slate-800">
        <div className="bg-amber-400 p-6 sm:p-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-950/30">
            <RouteIcon className="h-7 w-7 text-white" strokeWidth={1.8} />
          </div>
          <h2 className="font-display text-2xl font-bold text-white mb-2">Calculadora de Viagem</h2>
          <p className="text-slate-300 text-sm max-w-lg mx-auto">
            Descubra a distância exata, o custo com combustível, pedágio e o valor do PIX para rachar com os amigos.
          </p>
        </div>

        <div className="p-6 sm:p-8 grid gap-8 md:grid-cols-2">
          {/* Seção de Busca */}
          <div className="space-y-5">
            <h3 className="font-semibold text-white flex items-center gap-2 border-b border-slate-800 pb-2">
              <MapPin className="h-5 w-5 text-amber-500" />
              Rota
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Origem</label>
                <input
                  type="text"
                  placeholder="Ex: São Paulo, SP"
                  value={origemStr}
                  onChange={e => setOrigemStr(e.target.value)}
                  className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-3 text-sm focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Destino</label>
                <input
                  type="text"
                  placeholder="Ex: Ubatuba, SP"
                  value={destinoStr}
                  onChange={e => setDestinoStr(e.target.value)}
                  className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-3 text-sm focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
                />
              </div>

              <button
                onClick={handleRouteSearch}
                disabled={isSearching || !origemStr || !destinoStr}
                className="w-full flex items-center justify-center gap-2 rounded-xl bg-slate-900 py-3.5 text-sm font-semibold text-white transition-all hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSearching ? 'Calculando rota...' : 'Calcular Rota'}
                {!isSearching && <Search className="h-4 w-4" />}
              </button>
            </div>

            {distanceKm !== null && (
              <div className="mt-4 rounded-xl bg-amber-400/10 p-4 border border-amber-400/30 text-sm">
                <div className="flex items-center gap-2 font-semibold text-amber-400 mb-1">
                  <Navigation className="h-4 w-4" />
                  Rota traçada com sucesso
                </div>
                <div className="text-amber-400">
                  Distância: <strong>{distBase.toFixed(1)} km</strong>
                  <br/>
                  Tempo est.: <strong>{Math.floor(durationMin!/60)}h {durationMin!%60}m</strong>
                </div>
              </div>
            )}
          </div>

          {/* Configurações do Carro */}
          <div className="space-y-5">
            <h3 className="font-semibold text-white flex items-center gap-2 border-b border-slate-800 pb-2">
              <Car className="h-5 w-5 text-amber-500" />
              Veículo e Divisão
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Consumo (km/l)</label>
                <DecimalInput
                  value={consumo}
                  onChange={setConsumo}
                  className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white focus:border-amber-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Preço Gas/Eta (R$)</label>
                <DecimalInput
                  value={precoCombustivel}
                  onChange={setPrecoCombustivel}
                  className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white focus:border-amber-400 focus:outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Pedágios (R$)</label>
                <DecimalInput
                  value={pedagio}
                  onChange={setPedagio}
                  className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white focus:border-amber-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1 font-bold text-amber-400 flex items-center gap-1">
                  <Users className="h-4 w-4" /> Pessoas Roteando
                </label>
                <input
                  type="number"
                  min="1"
                  value={passageiros}
                  onChange={e => setPassageiros(Number(e.target.value))}
                  className="w-full rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-2.5 text-sm font-bold text-amber-400 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer mt-4">
              <input
                type="checkbox"
                checked={idaEVolta}
                onChange={e => setIdaEVolta(e.target.checked)}
                className="w-4 h-4 text-amber-500 rounded border-slate-700 focus:ring-amber-400"
              />
              <span className="text-sm font-medium text-slate-400">Calcular Ida e Volta</span>
            </label>
          </div>
        </div>

        {/* Resultado Final (se tivermos a rota calculada) */}
        {distanceKm !== null && (
          <div className="border-t border-slate-800 bg-slate-800 p-6 sm:p-8">
            <h3 className="font-display text-xl font-bold text-white text-center mb-6">
              Resumo da Viagem 💸
            </h3>

            <div className="grid sm:grid-cols-3 gap-6">
              <div className="bg-slate-900 rounded-xl p-5 border border-slate-700 text-center shadow-sm">
                <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1">Km Total</p>
                <div className="font-display text-2xl font-bold text-slate-300">
                  {distTotal.toFixed(0)} <span className="text-sm font-medium">km</span>
                </div>
              </div>
              <div className="bg-slate-900 rounded-xl p-5 border border-slate-700 text-center shadow-sm">
                <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1">Gasto Combustível</p>
                <div className="font-display text-2xl font-bold text-slate-300">
                  R$ {gastoCombustivel.toFixed(2)}
                </div>
              </div>
              <div className="bg-amber-400/10 rounded-xl p-5 border border-amber-400/30 text-center shadow-sm shadow-amber-900/20">
                <p className="text-xs text-amber-400 font-bold uppercase tracking-wider mb-1 flex justify-center items-center gap-1">
                  PIX por Pessoa
                </p>
                <div className="font-display text-3xl font-black text-amber-400">
                  R$ {perCapita.toFixed(2)}
                </div>
              </div>
            </div>
            {idaEVolta && (
              <p className="text-center text-xs text-slate-400 mt-4 flex items-center justify-center gap-1">
                <Info className="h-3 w-3" /> Custo total da viagem estimado em R$ {gastoTotal.toFixed(2)} (km + pedágios)
              </p>
            )}
          </div>
        )}
      </motion.div>

    </div>
  );
};

export default CalculadoraViagem;




